import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Archive, Layers, Plus, RotateCcw } from 'lucide-react'
import { useState } from 'react'
import { PageFrame, SectionHeading } from '@/components/PageFrame'
import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm'
import { EmptyState, Panel, Skeleton } from '@/components/ui/display'
import { createSpace, listSpaces, spaceCounts, setSpaceStatus, updateSpace, type SpaceDraft } from '@/data/spaces'
import type { Space } from '@/data/types'
import { cn } from '@/lib/utils'
import { SpaceEditor } from './SpaceEditor'

/**
 * Spaces, grouped by what they are rather than sorted into one list.
 *
 * A project and an area are read with different questions — "is this still
 * running?" versus "what is going on here?" — and interleaving them means every
 * row has to be classified before it can be read. Archived sits at the bottom
 * because it is a record, not a destination.
 */
export function SpacesPage() {
  const queryClient = useQueryClient()
  const confirm = useConfirm()

  const [editing, setEditing] = useState<string | 'new' | null>(null)

  const spaces = useQuery({ queryKey: ['spaces'], queryFn: listSpaces })

  // The chat's space picker and the meetings filter read the space list
  // synchronously as they render, so a new or renamed space has to invalidate
  // everything — otherwise they disagree with this page until a reload.
  const invalidate = () => queryClient.invalidateQueries()

  const create = useMutation({
    mutationFn: (draft: SpaceDraft) => createSpace(draft),
    onSuccess: () => {
      void invalidate()
      setEditing(null)
    },
  })

  const update = useMutation({
    mutationFn: ({ id, draft }: { id: string; draft: Partial<SpaceDraft> }) =>
      updateSpace(id, draft),
    onSuccess: () => {
      void invalidate()
      setEditing(null)
    },
  })

  const archive = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Space['status'] }) =>
      setSpaceStatus(id, status),
    onSuccess: () => void invalidate(),
  })

  const all = spaces.data ?? []
  const projects = all.filter((space) => space.status === 'active' && space.kind === 'project')
  const areas = all.filter((space) => space.status === 'active' && space.kind === 'area')
  const archived = all.filter((space) => space.status === 'archived')

  const busy = update.isPending || archive.isPending

  const section = (title: string, items: Space[]) =>
    items.length === 0 ? null : (
      <>
        <SectionHeading meta={`${items.length}`}>{title}</SectionHeading>
        <Panel>
          {items.map((space) => (
            <SpaceRow
              key={space.id}
              space={space}
              expanded={editing === space.id}
              busy={busy}
              onToggle={() => setEditing((current) => (current === space.id ? null : space.id))}
              onSave={(draft) => update.mutate({ id: space.id, draft })}
              onCancel={() => setEditing(null)}
              onArchive={async () => {
                const counts = spaceCounts(space.id)
                const ok = await confirm({
                  title: `Archive “${space.name}”?`,
                  body:
                    counts.meetings > 0
                      ? `${counts.meetings} meeting${counts.meetings === 1 ? '' : 's'} stay filed here and keep showing ${space.code}. The space stops appearing when filing something new.`
                      : 'It stops appearing when filing something new. You can restore it at any time.',
                  confirmLabel: 'Archive',
                })
                if (ok) archive.mutate({ id: space.id, status: 'archived' })
              }}
            />
          ))}
        </Panel>
      </>
    )

  return (
    <PageFrame
      title="Spaces"
      subtitle={
        spaces.isPending ? undefined : `${projects.length} projects · ${areas.length} areas`
      }
      actions={
        <Button
          size="sm"
          variant="primary"
          onClick={() => setEditing((current) => (current === 'new' ? null : 'new'))}
        >
          <Plus className="size-3.5" />
          New space
        </Button>
      }
    >
      {editing === 'new' ? (
        <Panel className="mt-3 border-[var(--color-border-strong)] bg-[var(--color-surface-sunken)] shadow-[inset_3px_0_0_var(--color-fg)]">
          {/* The kind cards directly below say what a project and an area are,
              at length. Repeating it in four words here only made the shorter
              version the one people read. */}
          <div className="px-3 py-2">
            <p className="text-xs font-medium text-[var(--color-fg)]">New space</p>
          </div>
          <SpaceEditor
            busy={create.isPending}
            onSave={(draft) => create.mutate(draft)}
            onCancel={() => setEditing(null)}
          />
        </Panel>
      ) : null}

      {spaces.isPending ? (
        <div className="space-y-1 pt-3">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      ) : all.length === 0 ? (
        <EmptyState
          icon={Layers}
          title="No spaces yet"
          description="Spaces are how meetings get filed — one per deal, client or workstream."
          action={
            <Button size="sm" variant="primary" onClick={() => setEditing('new')}>
              <Plus className="size-3.5" />
              New space
            </Button>
          }
        />
      ) : (
        <>
          {section('Projects', projects)}
          {section('Areas', areas)}

          {archived.length > 0 ? (
            <>
              <SectionHeading meta="not offered when filing">Archived</SectionHeading>
              <Panel>
                {archived.map((space) => (
                  <div
                    key={space.id}
                    className="flex items-center gap-2.5 border-b border-[var(--color-border-subtle)] px-3 py-2 last:border-b-0"
                  >
                    <span className="w-10 shrink-0 font-mono text-2xs text-[var(--color-fg-subtle)]">
                      {space.code}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-xs text-[var(--color-fg-muted)]">
                      {space.name}
                    </span>
                    <Button
                      size="xs"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => archive.mutate({ id: space.id, status: 'active' })}
                    >
                      <RotateCcw className="size-3" />
                      Restore
                    </Button>
                  </div>
                ))}
              </Panel>
            </>
          ) : null}
        </>
      )}
    </PageFrame>
  )
}

function SpaceRow({
  space,
  expanded,
  busy,
  onToggle,
  onSave,
  onCancel,
  onArchive,
}: {
  space: Space
  expanded: boolean
  busy?: boolean
  onToggle: () => void
  onSave: (draft: Partial<SpaceDraft>) => void
  onCancel: () => void
  onArchive: () => void
}) {
  return (
    <div
      className={cn(
        'group border-b last:border-b-0',
        expanded
          ? 'relative -mt-px border-y border-[var(--color-border-strong)] bg-[var(--color-surface-sunken)] shadow-[inset_3px_0_0_var(--color-fg)] first:mt-0'
          : 'border-[var(--color-border-subtle)]'
      )}
    >
      <div
        className={cn(
          'flex items-center gap-2.5 px-2.5',
          expanded ? '' : 'hover:bg-[var(--color-surface-hover)]'
        )}
      >
        <span className="grid h-5 w-10 shrink-0 place-items-center rounded-[var(--radius-xs)] border border-[var(--color-border)] bg-[var(--color-surface-sunken)] font-mono text-2xs tracking-tight text-[var(--color-fg-muted)]">
          {space.code}
        </span>

        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center py-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/35"
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs font-medium text-[var(--color-fg)]">
              {space.name}
            </span>
            {space.description ? (
              <span className="mt-0.5 block truncate text-2xs text-[var(--color-fg-subtle)]">
                {space.description}
              </span>
            ) : null}
          </span>
        </button>

        {/* One action on the row. Counts, member faces and a scope button were
            three pieces of metadata competing with the name for a line that is
            read down a column — and scoping is now a property of a chat rather
            than of the app, so it is chosen beside the composer. */}
        <Button
          size="iconSm"
          variant="ghost"
          disabled={busy}
          aria-label={`Archive ${space.name}`}
          className="shrink-0 hover:text-[var(--color-danger)]"
          onClick={onArchive}
        >
          <Archive className="size-3" />
        </Button>
      </div>

      {/* The expanded row is the space's SETTINGS and nothing else. What is
          filed in it is a list of meetings, and there is already a screen whose
          whole job is listing meetings — filter it by this space there rather
          than keeping a second, five-row copy here. */}
      {expanded ? (
        <SpaceEditor space={space} busy={busy} onSave={onSave} onCancel={onCancel} />
      ) : null}
    </div>
  )
}
